/**
 * ★ A PIECE THE AUTHOR CANNOT CHANGE OPENS AS A READ VIEW, NOT AS AN EDITOR THAT REFUSES (the owner,
 * 2026-10-05, on a published article: "its published but opens in editor like i could edit it with
 * all controls enabled and warning at top that it cant be edited - should not there be like a locked
 * / another view/UI?"; asked, he chose "A read view").
 *
 * Every state the author does not write in - in review, scheduled, published, taken down, archived
 * (`WorkingCopy.writableBy` is not `author`; EditorRoute.tsx's header, G19) - draws this instead of
 * the writing canvas and its panel of form controls. On the left the piece as a reader meets it: the
 * cover as a picture, the title, the standfirst, the reading time, the blocks drawn as text
 * (./ReadOnlyText.tsx) - no frame to drop a picture in, no field, nothing to type into. On the right
 * the facts about it: where it stands and since when, its category and tags, the link to it on the
 * website, its figures beside the way to Statistics, its comments, an editor's notes if there are
 * any, and its versions (the owner, 2026-10-20: the link, the category and tags, the figures and the
 * comments, and no "Something to correct?"). In review it says it is waiting for the verdict.
 * At the very foot of a PUBLISHED article's column, "Archive this article" (./ArchiveThisArticle.tsx,
 * design 24, T2): the list card no longer offers it, and on a phone it is the last thing on the page.
 * A version read from "Version history" opens where the article is, in its place, until "Back to
 * the current version" (./HistoryPanel.tsx's `VersionInColumn`, design 24, T20).
 *
 * ★ THE DATE COMES FROM THE STATISTICS LIST, NOT THE WORKING COPY, which carries none:
 * `listArticleStats` has one row per article the author has published, with `publishedAt` whatever
 * its range, so the row is found by paging the default range. THE FIGURES AND THE LINK ARE
 * `getArticleSummary`'s - since the article went out, not over a range. A piece never published has
 * neither, and then nothing is drawn - nothing is guessed, and a read that fails costs those lines,
 * not the screen.
 */
import { Anchor, Stack, Text, Title } from '@mantine/core';
import { useCallback, useState } from 'react';
import { useCabinetApi } from '../../../features/cabinet/api';
import type {
  ArticleStats,
  ArticleStatsPage,
  OperationResult,
} from '../../../features/cabinet/api';
import { StatusPill } from '../../../features/cabinet/components';
import {
  formatCount,
  formatDay,
  formatInstant,
  formatMoney,
} from '../../../features/cabinet/format';
import { WEBSITE_ORIGIN } from '../../../features/auth/handoff/website-handoff';
import { commentsHrefFor } from '../../comments/comments-href';
import { useAsync } from '../../../features/cabinet/state';
import type { AsyncResult } from '../../../features/cabinet/state';
import { emulatorImageUrl, servedUrlOnError } from '../../../features/storage/emulator-image-url';
import { InAppAnchor } from '../../../features/shell/InAppAnchor';
import type {
  Block,
  EditorCategory,
  ReviewRecord,
  VersionList,
} from '../../../features/cabinet/api/editor-api';
import { useTranslation } from '../../../i18n';
import { articlesT } from '../articles-i18n';
import type { ArticlesT } from '../articles-i18n';
import { ArchiveThisArticle } from './ArchiveThisArticle';
import { CalendarLinksPanel } from './CalendarLinksPanel';
import { HistoryPanel, VersionInColumn } from './HistoryPanel';
import type { OpenedVersion } from './HistoryPanel';
import { BlockVideoPlayer } from './VideoBlockPlayer';
import { ReadOnlyText } from './ReadOnlyText';
import { ReviewNotes } from './ReviewNotes';
import type { DraftBase, EditorDraft } from './document';
import editorClasses from './editor.module.css';
import classes from './read-view.module.css';

/** The list this screen is reached from, and the two other places it points to. */
const ARTICLES_HREF = '/articles';
const STATISTICS_HREF = '/articles/statistics';

/** What an article in review says, in place of anything to change. */
export function inReviewNote(t: ArticlesT = articlesT()): string {
  return t('editor.readView.inReview');
}

/** A stop, not a claim: an author's published pieces at 100 a page. */
const STATS_PAGE_BUDGET = 10;

export interface ArticleReadViewProps {
  readonly base: DraftBase;
  readonly draft: EditorDraft;
  readonly categories: readonly EditorCategory[];
  readonly versions: AsyncResult<VersionList>;
  readonly review: ReviewRecord | undefined;
  readonly words: number;
  readonly minutes: number;
}

export function ArticleReadView({
  base,
  draft,
  categories,
  versions,
  review,
  words,
  minutes,
}: ArticleReadViewProps) {
  const { t } = useTranslation('articles');
  const figures = usePublishedFigures(base.articleId, base.status);
  const summary = useArticleSummary(base.articleId, base.status);
  const [reading, setReading] = useState<OpenedVersion | undefined>(undefined);
  const title = draft.title.trim().length === 0 ? t('common.untitled') : draft.title;
  const categoryCode = draft.addressCategory ?? draft.categories[0] ?? null;
  const category =
    categoryCode === null
      ? null
      : (categories.find((item) => item.code === categoryCode)?.label ?? categoryCode);
  const webUrl =
    summary?.webPath === null || summary?.webPath === undefined
      ? null
      : `${WEBSITE_ORIGIN}${summary.webPath}`;
  const findings = review?.findings ?? [];
  const when = whenLine(base.status, draft.plannedGoLiveAt, figures?.publishedAt ?? null, t);

  return (
    <Stack gap="lg">
      {/* THE TOP ROW: where you are and where the piece stands - nothing to save, nothing to send. */}
      <div className={editorClasses.top}>
        <div className={editorClasses.crumbs}>
          <InAppAnchor href={ARTICLES_HREF} className={editorClasses.crumbLink} underline="hover">
            {t('common.articles')}
          </InAppAnchor>
          <span aria-hidden="true">›</span>
          <h1 className={editorClasses.crumbTitle}>{title}</h1>
        </div>
        <div className={editorClasses.actions}>
          <StatusPill status={base.status} />
          {when === null ? null : <span className={classes.date}>{when}</span>}
        </div>
      </div>

      <div className={editorClasses.layout}>
        <div className={editorClasses.grid}>
          {/* ------------------------------------------------------------- the piece, as read */}
          {reading === undefined ? null : (
            <VersionInColumn
              key={`${String(reading.revisionNo)}:${reading.language ?? ''}`}
              articleId={base.articleId}
              version={reading}
              backLabel={t('editor.readView.backToCurrent')}
              onBack={() => {
                setReading(undefined);
              }}
            />
          )}
          <article
            className={editorClasses.canvas}
            aria-label={t('editor.readView.articleLabel')}
            hidden={reading !== undefined}
          >
            {base.coverUrl === null ? null : (
              <img
                className={classes.cover}
                src={emulatorImageUrl(base.coverUrl)}
                onError={servedUrlOnError(base.coverUrl)}
                alt=""
              />
            )}
            <h2 className={classes.title}>{title}</h2>
            {draft.subtitle.trim().length === 0 ? null : (
              <p className={classes.standfirst}>{draft.subtitle}</p>
            )}
            <div className={classes.meta}>
              <span>{t('common.words', { count: words, value: formatCount(words) })}</span>
              {minutes === 0 ? null : <span>{t('common.minRead', { count: minutes })}</span>}
            </div>
            <div className={classes.body}>
              {draft.blocks.map((block) => (
                <ReadBlock key={block.blockId} block={block} articleId={base.articleId} />
              ))}
            </div>
          </article>

          {/* --------------------------------------------------------------- the facts */}
          <aside className={editorClasses.panel} aria-label={t('common.aboutThisArticle')}>
            <Stack gap="lg">
              <Stack gap="sm">
                <Title order={2} size="h5">
                  {t('common.aboutThisArticle')}
                </Title>
                <dl className={classes.facts}>
                  {draft.writtenWithAi ? (
                    <>
                      <dt>{t('editor.readView.ai')}</dt>
                      <dd>{t('editor.setup.writtenWithAi')}</dd>
                    </>
                  ) : null}
                  {category === null ? null : (
                    <>
                      <dt>{t('editor.readView.category')}</dt>
                      <dd>
                        <span className={classes.tag}>{category}</span>
                      </dd>
                    </>
                  )}
                  {draft.tags.length === 0 ? null : (
                    <>
                      <dt>{t('editor.readView.tags')}</dt>
                      <dd>
                        <span className={classes.tags}>
                          {draft.tags.map((tag) => (
                            <span key={tag} className={classes.tag}>
                              {tag}
                            </span>
                          ))}
                        </span>
                      </dd>
                    </>
                  )}
                  {webUrl === null ? null : (
                    <>
                      <dt>{t('editor.readView.link')}</dt>
                      <dd>
                        {/* The live page in a new tab, as "Your public page address" opens one. */}
                        <Anchor href={webUrl} target="_blank" rel="noopener" size="sm">
                          {webUrl.replace(/^https?:\/\//, '')}
                        </Anchor>
                      </dd>
                    </>
                  )}
                </dl>
                {summary === undefined ? null : (
                  <div className={classes.figures}>
                    <span>
                      {t('editor.readView.figures.views', {
                        count: summary.views,
                        value: formatCount(summary.views),
                      })}
                    </span>
                    <span>
                      {t('editor.readView.figures.likes', {
                        count: summary.likes,
                        value: formatCount(summary.likes),
                      })}
                    </span>
                    <span>
                      {t('editor.readView.figures.comments', {
                        count: summary.comments,
                        value: formatCount(summary.comments),
                      })}
                    </span>
                    <span>{tipsWords(summary, t)}</span>
                    <InAppAnchor href={STATISTICS_HREF} size="sm">
                      {t('editor.readView.seeStatistics')}
                    </InAppAnchor>
                  </div>
                )}
              </Stack>

              {summary === undefined ? null : (
                <Stack component="section" gap={6} aria-labelledby="read-view-comments">
                  <Title id="read-view-comments" order={2} size="h5">
                    {t('editor.readView.comments.title')}
                  </Title>
                  <Text size="sm">
                    {summary.comments === 0
                      ? t('editor.readView.comments.none')
                      : t('editor.readView.comments.fromReaders', {
                          count: summary.comments,
                          value: formatCount(summary.comments),
                        })}
                  </Text>
                  {summary.comments === 0 ? null : (
                    <InAppAnchor href={commentsHrefFor(base.articleId)} size="sm">
                      {t('editor.readView.comments.goTo')}
                    </InAppAnchor>
                  )}
                </Stack>
              )}

              {base.status === 'in_review' ? (
                <div className={classes.note}>
                  <Text size="sm">{inReviewNote(t)}</Text>
                </div>
              ) : null}

              {/* The calendar events it is linked to - a list here, with no way to change it. */}
              <CalendarLinksPanel
                articleId={base.articleId}
                disabled
                plannedGoLiveAt={draft.plannedGoLiveAt}
                publishDates={[]}
              />

              {review === undefined || findings.length === 0 ? null : (
                <Stack gap="sm">
                  <Title order={2} size="h5">
                    {t('common.editorNotes')}
                  </Title>
                  <ReviewNotes
                    review={review}
                    blocks={draft.blocks}
                    onResolve={() => undefined}
                    resolvingOrdinal={undefined}
                    disabled
                  />
                </Stack>
              )}

              <HistoryPanel
                articleId={base.articleId}
                versions={versions}
                canKeep={false}
                saveFirst={() => Promise.resolve(false)}
                reading={reading}
                onRead={setReading}
              />

              {base.status === 'published' ? (
                <ArchiveThisArticle articleId={base.articleId} />
              ) : null}
            </Stack>
          </aside>
        </div>
      </div>
    </Stack>
  );
}

function ReadBlock({ block, articleId }: { readonly block: Block; readonly articleId: string }) {
  switch (block.kind) {
    case 'text':
    case 'quote':
      return <ReadOnlyText block={block} />;
    case 'image':
      return block.url === undefined ? null : (
        <figure className={classes.figure}>
          <img
            src={emulatorImageUrl(block.url)}
            onError={servedUrlOnError(block.url)}
            alt={block.imageAlt ?? ''}
          />
          {block.caption === null || block.caption === undefined ? null : (
            <figcaption className={classes.caption}>{block.caption}</figcaption>
          )}
        </figure>
      );
    case 'divider':
      return <hr className={classes.divider} />;
    case 'video':
      return <BlockVideoPlayer articleId={articleId} blockId={block.blockId} video={block.video} />;
    default:
      return null;
  }
}

/** Since when it stands where it stands, in words - `null` where nothing dated is known. */
function whenLine(
  status: DraftBase['status'],
  plannedGoLiveAt: string | null,
  publishedAt: string | null,
  t: ArticlesT,
): string | null {
  if (status === 'scheduled' && plannedGoLiveAt !== null) {
    return t('editor.readView.goesLive', { when: formatInstant(plannedGoLiveAt) });
  }
  if ((status === 'published' || status === 'unpublished') && publishedAt !== null) {
    return t('editor.readView.published', { when: formatDay(publishedAt.slice(0, 10)) });
  }
  return null;
}

/** The tips sent on it, as money, or "No tips yet" while none has been credited. */
function tipsWords(summary: ArticleSummary, t: ArticlesT): string {
  return summary.tipsCurrency === null || summary.tipsCurrency === undefined
    ? t('editor.readView.figures.noTips')
    : t('editor.readView.figures.tips', {
        amount: formatMoney(summary.tipsMinor, summary.tipsCurrency),
      });
}

type ArticleSummary = OperationResult<'getArticleSummary'>;

/**
 * The piece's figures since it went out and its link on the website (`getArticleSummary`), for a
 * piece that has ever been published; else `undefined` - in review or scheduled nothing has come
 * in yet - and a read that fails costs these lines, not the screen.
 */
function useArticleSummary(
  articleId: string,
  status: DraftBase['status'],
): ArticleSummary | undefined {
  const api = useCabinetApi();
  const everPublished = status === 'published' || status === 'unpublished' || status === 'archived';
  const read = useCallback(
    async (signal: AbortSignal): Promise<ArticleSummary | null> =>
      everPublished ? await api.getArticleSummary(articleId, signal) : null,
    [api, articleId, everPublished],
  );
  const summary = useAsync(read, `read-view:summary:${articleId}`);
  return summary.data ?? undefined;
}

interface PublishedFigures {
  readonly publishedAt: string | null;
}

/** The piece's row of the statistics list, for a piece that has one; else `undefined`. */
function usePublishedFigures(
  articleId: string,
  status: DraftBase['status'],
): PublishedFigures | undefined {
  const api = useCabinetApi();
  const everPublished = status === 'published' || status === 'unpublished' || status === 'archived';
  const read = useCallback(
    async (signal: AbortSignal): Promise<PublishedFigures | null> => {
      if (!everPublished) {
        return null;
      }
      let cursor: string | undefined;
      for (let page = 0; page < STATS_PAGE_BUDGET; page += 1) {
        // The page as the statistics screen types it (ArticleStatsRoute.tsx): the generated result
        // composes the shared envelope, so its items are narrowed by the named page type.
        const answer = (await api.listArticleStats(
          { limit: 100, ...(cursor === undefined ? {} : { cursor }) },
          signal,
        )) as ArticleStatsPage;
        const row: ArticleStats | undefined = answer.items.find(
          (item: ArticleStats) => item.articleId === articleId,
        );
        if (row !== undefined) {
          return { publishedAt: row.publishedAt ?? null };
        }
        cursor = answer.next_cursor ?? undefined;
        if (cursor === undefined) {
          return null;
        }
      }
      return null;
    },
    [api, articleId, everPublished],
  );
  const figures = useAsync(read, `read-view:figures:${articleId}`);
  return figures.data ?? undefined;
}
